import { useState } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Input,
  Label,
  Text,
  Dropdown,
  Option,
  mergeClasses,
} from '@fluentui/react-components';
import {
  FilterFilled,
  DismissFilled,
  SearchRegular,
} from '@fluentui/react-icons';
import type { RunStatus, RunFilters, VersionFilter } from '@/types/monitoring';

type RunsFiltersProps = {
  filters: RunFilters;
  onFiltersChange: (filters: Partial<RunFilters>) => void;
  onClearFilters: () => void;
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  filterIcon: {
    color: tokens.colorNeutralForeground3,
  },
  clearButton: {
    minWidth: 'auto',
    padding: tokens.spacingHorizontalXS,
  },
  filtersGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  fieldLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  statusOptions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
  statusButton: {
    minWidth: 'auto',
  },
  activeFiltersCount: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderRadius: tokens.borderRadiusCircular,
    padding: `0 ${tokens.spacingHorizontalXS}`,
    fontSize: tokens.fontSizeBase100,
    marginLeft: tokens.spacingHorizontalXS,
  },
  filterToggle: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
  },
});

const STATUS_OPTIONS: { value: RunStatus; label: string }[] = [
  { value: 'running', label: 'Running' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const VERSION_OPTIONS: { value: VersionFilter; label: string }[] = [
  { value: 'all', label: 'All Versions' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
];

export const RunsFilters = ({
  filters,
  onFiltersChange,
  onClearFilters,
}: RunsFiltersProps) => {
  const styles = useStyles();
  const [isExpanded, setIsExpanded] = useState(true);

  const activeFiltersCount = [
    filters.statuses.length > 0,
    !!filters.searchQuery,
    !!filters.dateRange?.start || !!filters.dateRange?.end,
    filters.durationRange?.min !== undefined || filters.durationRange?.max !== undefined,
    filters.versionFilter && filters.versionFilter !== 'all',
  ].filter(Boolean).length;

  const handleStatusToggle = (status: RunStatus) => {
    const newStatuses = filters.statuses.includes(status)
      ? filters.statuses.filter((s) => s !== status)
      : [...filters.statuses, status];
    onFiltersChange({ statuses: newStatuses });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ searchQuery: e.target.value || undefined });
  };

  const handleVersionChange = (_: unknown, data: { optionValue?: string | undefined }) => {
    const value = (data.optionValue as VersionFilter | undefined) ?? 'all';
    onFiltersChange({ versionFilter: value === 'all' ? undefined : value });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button
          className={mergeClasses(styles.headerTitle, styles.filterToggle)}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <FilterFilled className={styles.filterIcon} />
          <Text>Filters</Text>
          {activeFiltersCount > 0 && (
            <span className={styles.activeFiltersCount}>{activeFiltersCount}</span>
          )}
        </button>
        {activeFiltersCount > 0 && (
          <Button
            appearance="subtle"
            icon={<DismissFilled />}
            className={styles.clearButton}
            onClick={onClearFilters}
            aria-label="Clear all filters"
            title="Clear all filters"
          />
        )}
      </div>

      {isExpanded && (
        <div className={styles.filtersGrid}>
          <div className={styles.field}>
            <Label className={styles.fieldLabel}>Search</Label>
            <Input
              placeholder="Search runs..."
              value={filters.searchQuery ?? ''}
              onChange={handleSearchChange}
              contentBefore={<SearchRegular />}
            />
          </div>

          <div className={styles.field}>
            <Label className={styles.fieldLabel}>Version</Label>
            <Dropdown
              placeholder="All Versions"
              value={VERSION_OPTIONS.find(v => v.value === (filters.versionFilter ?? 'all'))?.label ?? 'All Versions'}
              selectedOptions={[filters.versionFilter ?? 'all']}
              onOptionSelect={handleVersionChange}
            >
              {VERSION_OPTIONS.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Dropdown>
          </div>

          <div className={styles.field}>
            <Label className={styles.fieldLabel}>Status</Label>
            <div className={styles.statusOptions}>
              {STATUS_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  appearance={filters.statuses.includes(option.value) ? 'primary' : 'subtle'}
                  size="small"
                  className={styles.statusButton}
                  onClick={() => handleStatusToggle(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>

        </div>
      )}
    </div>
  );
};
